import _slicedToArray from "/home/vsts/work/1/s/packages/husky-site/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/home/vsts/work/1/s/packages/husky-site/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import "./index.css";
import { jsxs, jsx } from 'react/jsx-runtime';
import { Button } from '@design-system/button';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { forwardRef, useRef, useState, useCallback, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Icon } from '@design-system/icon';
import { SearchInput } from '@design-system/search-input';
import { Typography } from '@design-system/typography';
import { isBrowser } from '@phx-husky/browser-helpers';
import { getLocalStorageData, setLocalStorageData } from '@phx-husky/local-storage';
import { useScreenRecognition } from '@phx-husky/use-screen-recognition';
var readUserSearches = function readUserSearches(localStorageLastSearchesKey) {
  return isBrowser() ? getLocalStorageData(localStorageLastSearchesKey) : null;
};
var restoreUserSearches = function restoreUserSearches(localStorageData) {
  return localStorageData === null ? [] : localStorageData;
};
var writeUserSearches = function writeUserSearches(lastUserSearches, localStorageLastSearchesKey) {
  return setLocalStorageData(localStorageLastSearchesKey, lastUserSearches);
};
var isAlreadyAdded = function isAlreadyAdded(lastUserSearches, searchRequest) {
  return lastUserSearches.indexOf(searchRequest);
};
var formatUserSearchWithExistingRequest = function formatUserSearchWithExistingRequest(searchRequest, lastUserSearches, foundIndex) {
  return [searchRequest].concat(_toConsumableArray(lastUserSearches.slice(0, foundIndex)), _toConsumableArray(lastUserSearches.slice(foundIndex + 1)));
};
var getUserSearches = function getUserSearches(localStorageLastSearchesKey) {
  return restoreUserSearches(readUserSearches(localStorageLastSearchesKey));
};
var saveUserSearch = function saveUserSearch(searchRequest, lastSearchesQuantity, localStorageLastSearchesKey) {
  if (!searchRequest) return;
  var lastUserSearches = getUserSearches(localStorageLastSearchesKey);
  var foundIndex = isAlreadyAdded(lastUserSearches, searchRequest);
  var newSearches = foundIndex !== -1 ? formatUserSearchWithExistingRequest(searchRequest, lastUserSearches, foundIndex) : [searchRequest].concat(_toConsumableArray(lastUserSearches.slice(0, lastSearchesQuantity - 1)));
  writeUserSearches(newSearches, localStorageLastSearchesKey);
};
var handleNavigation = function handleNavigation(searchText, customNavigate) {
  if (customNavigate) {
    customNavigate("?q=".concat(searchText));
  } else {
    window.location.href = new URL("?q=".concat(searchText), document.baseURI).href;
  }
};
var SearchBlockContainer = forwardRef(function (_ref, ref) {
  var ariaLabelClose = _ref.ariaLabelClose,
    _onClick = _ref.onClick,
    searchInputPlaceholder = _ref.searchInputPlaceholder,
    swipeLabel = _ref.swipeLabel,
    isMobile = _ref.isMobile,
    userSearches = _ref.userSearches,
    handleSetUserSearches = _ref.handleSetUserSearches,
    customNavigate = _ref.customNavigate,
    localStorageLastSearchesKey = _ref.localStorageLastSearchesKey,
    lastSearchesQuantity = _ref.lastSearchesQuantity,
    lastSearchesTitle = _ref.lastSearchesTitle,
    searchButtonText = _ref.searchButtonText,
    searchPageUrl = _ref.searchPageUrl,
    autocomplete = _ref.autocomplete,
    handleBurgerMenu = _ref.handleBurgerMenu;
  var _a, _b;
  var inputRef = useRef(null);
  var handleSearch = function handleSearch() {
    var searchText = inputRef.current.value;
    saveUserSearch(searchText, lastSearchesQuantity, localStorageLastSearchesKey);
    handleSetUserSearches();
    handleNavigation(searchText, customNavigate);
    _onClick();
    handleBurgerMenu === null || handleBurgerMenu === void 0 ? void 0 : handleBurgerMenu();
  };
  var handleKeyDown = function handleKeyDown(event) {
    if (!inputRef.current || event.key !== 'Enter') return;
    handleSearch();
  };
  return jsxs("div", Object.assign({
    ref: ref,
    "data-testid": "search-block-container"
  }, {
    children: [jsx(Button, Object.assign({
      className: "cds-search-block__close",
      variant: "subtle-icon",
      size: "large",
      iconPrefix: {
        props: {
          icon: 'close'
        }
      },
      onClick: _onClick
    }, {
      children: ariaLabelClose
    })), jsxs("div", Object.assign({
      className: "cds-search-block__search-bar-container"
    }, {
      children: [jsxs("div", Object.assign({
        className: "cds-search-block__input-container"
      }, {
        children: [jsx(Icon, {
          icon: "search"
        }), jsx(SearchInput, {
          onKeyDown: handleKeyDown,
          placeholder: searchInputPlaceholder,
          inputRef: inputRef,
          searchPageUrl: searchPageUrl,
          autocomplete: autocomplete
        })]
      })), jsx("div", Object.assign({
        className: "cds-search-block__search-btn-container"
      }, {
        children: jsx(Button, Object.assign({
          size: "large",
          variant: "secondary",
          className: "cds-search-block__search-btn",
          onClick: handleSearch
        }, {
          children: searchButtonText
        }))
      })), (userSearches === null || userSearches === void 0 ? void 0 : userSearches.length) && ((_b = (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.length) < 3 ? jsxs("div", Object.assign({
        className: "cds-search-block__last-searches-container"
      }, {
        children: [jsx(Typography, {
          className: "cds-search-block__last-searches-title",
          variant: "headline",
          size: "s",
          content: lastSearchesTitle
        }), userSearches.map(function (search) {
          return jsx(Button, Object.assign({
            id: "search-button",
            size: "small",
            variant: "secondary",
            iconPrefix: {
              props: {
                icon: 'search'
              }
            },
            className: "cds-search-block__last-search",
            onClick: function onClick() {
              handleNavigation(search, customNavigate);
              _onClick();
              handleBurgerMenu === null || handleBurgerMenu === void 0 ? void 0 : handleBurgerMenu();
            }
          }, {
            children: search
          }), search);
        })]
      })) : null]
    })), isMobile ? jsxs("div", Object.assign({
      className: "cds-search-block__swipe"
    }, {
      children: [jsx(Icon, {
        icon: "arrowUp"
      }), jsx(Button, Object.assign({
        size: "large",
        variant: "subtle",
        onClick: _onClick
      }, {
        children: swipeLabel
      }))]
    })) : null]
  }));
});
var useBrowserScreen = function useBrowserScreen() {
  var _useScreenRecognition = useScreenRecognition(),
    isMobile = _useScreenRecognition.isMobile;
  var isMobileView = isBrowser() && isMobile;
  return {
    isMobileView: isMobileView
  };
};
var SearchBlock = forwardRef(function (_ref2, ref) {
  var className = _ref2.className,
    ariaLabelOpen = _ref2.ariaLabelOpen,
    ariaLabelClose = _ref2.ariaLabelClose,
    swipeLabel = _ref2.swipeLabel,
    searchInputPlaceholder = _ref2.searchInputPlaceholder,
    searchLabel = _ref2.searchLabel,
    customNavigate = _ref2.customNavigate,
    localStorageLastSearchesKey = _ref2.localStorageLastSearchesKey,
    lastSearchesQuantity = _ref2.lastSearchesQuantity,
    lastSearchesTitle = _ref2.lastSearchesTitle,
    searchButtonText = _ref2.searchButtonText,
    autocomplete = _ref2.autocomplete,
    searchPageUrl = _ref2.searchPageUrl,
    handleBurgerMenu = _ref2.handleBurgerMenu;
  var _useState = useState(getUserSearches(localStorageLastSearchesKey)),
    _useState2 = _slicedToArray(_useState, 2),
    userSearches = _useState2[0],
    setUserSearches = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSearchVisible = _useState4[0],
    setIsSearchVisible = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isSwipe = _useState6[0],
    setIsSwipe = _useState6[1];
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    touchStart = _useState8[0],
    setTouchStart = _useState8[1];
  var _useState9 = useState(0),
    _useState10 = _slicedToArray(_useState9, 2),
    touchEnd = _useState10[0],
    setTouchEnd = _useState10[1];
  var blockRef = useRef(null);
  var _useBrowserScreen = useBrowserScreen(),
    isMobile = _useBrowserScreen.isMobileView;
  var defaultBlockClass = 'cds-search-block__container';
  var activeBlockClass = 'cds-search-block__container cds-search-block__container--active';
  var mobileBlockClass = 'cds-search-block__container cds-search-block__container--mobile';
  var enterBlockClasses = isSwipe ? mobileBlockClass : activeBlockClass;
  var handleClickOutside = useCallback(function (event) {
    var _a;
    if (!((_a = blockRef === null || blockRef === void 0 ? void 0 : blockRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
      setIsSearchVisible(false);
    }
  }, [blockRef]);
  useEffect(function () {
    document.addEventListener('mousedown', handleClickOutside);
    return function () {
      return document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [blockRef]);
  var onClick = useCallback(function () {
    setIsSearchVisible(!isSearchVisible);
    if (isMobile) {
      setIsSwipe(!isSwipe);
    }
  }, [isSearchVisible, isMobile, setIsSwipe]);
  var handleTouch = useCallback(function (event) {
    if (event.type === 'touchstart') {
      setTouchStart(event.targetTouches[0].clientY);
      setTouchEnd(event.targetTouches[0].clientY);
    }
    if (event.type === 'touchmove') {
      setTouchEnd(event.targetTouches[0].clientY);
    }
  }, []);
  var handleTouchEnd = useCallback(function () {
    if (isMobile && touchStart > 150 && touchStart > touchEnd) {
      onClick();
    }
  }, [isMobile, touchStart, touchEnd, onClick]);
  var handleSetUserSearches = useCallback(function () {
    setUserSearches(getUserSearches(localStorageLastSearchesKey));
  }, [localStorageLastSearchesKey]);
  return jsxs("div", Object.assign({
    ref: ref,
    onTouchStart: handleTouch,
    onTouchMove: handleTouch,
    onTouchEnd: handleTouchEnd,
    className: classNames('cds-search-block', className),
    "data-testid": "cds-search-block"
  }, {
    children: [jsx(Button, Object.assign({
      variant: searchLabel ? 'nav' : 'subtle-icon',
      iconSuffix: {
        props: {
          icon: 'search'
        }
      },
      "aria-label": ariaLabelOpen,
      onClick: onClick,
      className: "cds-btn__search-icon"
    }, {
      children: searchLabel || ''
    })), jsx(CSSTransition, Object.assign({
      nodeRef: blockRef,
      in: isSearchVisible,
      timeout: 300,
      classNames: {
        appear: defaultBlockClass,
        appearActive: defaultBlockClass,
        appearDone: defaultBlockClass,
        enter: enterBlockClasses,
        enterDone: enterBlockClasses,
        exit: defaultBlockClass,
        exitActive: defaultBlockClass,
        exitDone: defaultBlockClass
      },
      unmountOnExit: true
    }, {
      children: jsx(FocusTrap, {
        children: jsx(SearchBlockContainer, {
          ariaLabelClose: ariaLabelClose,
          ariaLabelOpen: ariaLabelOpen,
          isMobile: isMobile,
          onClick: onClick,
          ref: blockRef,
          searchInputPlaceholder: searchInputPlaceholder,
          swipeLabel: swipeLabel,
          userSearches: userSearches,
          handleSetUserSearches: handleSetUserSearches,
          customNavigate: customNavigate,
          localStorageLastSearchesKey: localStorageLastSearchesKey,
          lastSearchesQuantity: lastSearchesQuantity,
          lastSearchesTitle: lastSearchesTitle,
          searchButtonText: searchButtonText,
          autocomplete: autocomplete,
          searchPageUrl: searchPageUrl,
          handleBurgerMenu: handleBurgerMenu
        })
      })
    })), jsx("div", {
      className: classNames('cds-search-block__mask', {
        'cds-search-block__mask--active': isSearchVisible
      })
    })]
  }));
});
export { SearchBlock };